import { ReactNode } from 'react';
import { PageLayout } from '../types';
import { ImageBlock as ImageBlockType } from '../types/images';
import type { VisualBlock as VisualBlockType } from '../types/visualizations';
import ImageBlock from './ImageBlock';
import VisualBlockComponent from './VisualBlock';
import { ImageIcon } from 'lucide-react';

interface LayoutRendererProps {
  layout: PageLayout;
  content: ReactNode;
  contentString?: string; // Raw text content for splitting in middle layout
  images: ImageBlockType[];
  visualBlocks?: VisualBlockType[];
  sectionId: number;
  onImageEdit?: () => void;
}

/**
 * LayoutRenderer - Renders content and images in various page layouts
 * Uses CSS float for text wrapping around images
 */
export default function LayoutRenderer({
  layout,
  content,
  contentString,
  images,
  visualBlocks = [],
  sectionId,
  onImageEdit,
}: LayoutRendererProps) {
  const primaryImage = images[0];
  const primaryVisual = visualBlocks[0];

  // Placeholder for when layout expects image but none exists
  const ImagePlaceholder = ({ float }: { float?: 'left' | 'right' }) => (
    <div
      className={`
        ${float === 'left' ? 'float-left mr-4 mb-2' : float === 'right' ? 'float-right ml-4 mb-2' : ''}
        w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg
        flex flex-col items-center justify-center text-gray-400 bg-gray-50
      `}
    >
      <ImageIcon size={24} className="mb-1 opacity-50" />
      <span className="text-xs">Image</span>
    </div>
  );

  const renderImage = (float: 'left' | 'right' | 'none' = 'none') => {
    const floatClasses = {
      left: 'float-left mr-4 mb-2',
      right: 'float-right ml-4 mb-2',
      none: '',
    };

    if (!primaryImage) {
      // Show placeholder only for layouts that expect images
      if (float !== 'none') {
        return <ImagePlaceholder float={float} />;
      }
      return null;
    }

    return (
      <div className={`${floatClasses[float]} max-w-[40%]`}>
        <ImageBlock
          block={primaryImage}
          sectionId={sectionId}
          onEdit={onImageEdit}
        />
      </div>
    );
  };

  switch (layout) {
    case 'image-right':
      return (
        <div className="p-6 clearfix">
          {renderImage('right')}
          {content}
        </div>
      );

    case 'image-left':
      return (
        <div className="p-6 clearfix">
          {renderImage('left')}
          {content}
        </div>
      );

    case 'image-header':
      return (
        <div className="p-6">
          {primaryImage ? (
            <div className="mb-4">
              <ImageBlock
                block={primaryImage}
                sectionId={sectionId}
                onEdit={onImageEdit}
              />
            </div>
          ) : (
            <div className="mb-4 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 bg-gray-50">
              <ImageIcon size={24} className="mr-2 opacity-50" />
              <span className="text-sm">Header image will appear here</span>
            </div>
          )}
          {content}
        </div>
      );

    case 'image-footer':
      return (
        <div className="p-6">
          {content}
          {primaryImage ? (
            <div className="mt-4">
              <ImageBlock
                block={primaryImage}
                sectionId={sectionId}
                onEdit={onImageEdit}
              />
            </div>
          ) : (
            <div className="mt-4 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 bg-gray-50">
              <ImageIcon size={24} className="mr-2 opacity-50" />
              <span className="text-sm">Footer image will appear here</span>
            </div>
          )}
        </div>
      );

    case 'two-column':
      return (
        <div className="p-6 columns-2 gap-8">
          {content}
        </div>
      );

    case 'image-middle': {
      // Split content for middle layout
      const text = contentString || '';
      const paragraphs = text.split('\n\n');
      const aboveContent = paragraphs[0] || '';
      const belowContent = paragraphs.slice(1).join('\n\n');

      return (
        <div className="p-6">
          {/* Text above */}
          {aboveContent && (
            <div className="mb-4 whitespace-pre-wrap text-gray-800 leading-relaxed">
              {aboveContent}
            </div>
          )}

          {/* Visual element in middle */}
          <div className="flex justify-center my-6">
            {primaryVisual ? (
              <div className="max-w-[600px] w-full">
                <VisualBlockComponent
                  block={primaryVisual}
                  sectionId={sectionId}
                  onEdit={onImageEdit}
                />
              </div>
            ) : primaryImage ? (
              <div className="max-w-[600px] w-full">
                <ImageBlock
                  block={primaryImage}
                  sectionId={sectionId}
                  onEdit={onImageEdit}
                />
              </div>
            ) : (
              <div className="w-full max-w-[400px] h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center text-gray-400 bg-gray-50">
                <ImageIcon size={32} className="mb-2 opacity-50" />
                <span className="text-sm">Visual element will appear here</span>
                <span className="text-xs mt-1">Add a chart, table, or image</span>
              </div>
            )}
          </div>

          {/* Text below */}
          {belowContent && (
            <div className="mt-4 whitespace-pre-wrap text-gray-800 leading-relaxed">
              {belowContent}
            </div>
          )}

          {/* Additional visual blocks */}
          {visualBlocks.slice(1).map((block) => (
            <div key={block.id} className="mt-4">
              <VisualBlockComponent
                block={block}
                sectionId={sectionId}
                onEdit={onImageEdit}
              />
            </div>
          ))}
        </div>
      );
    }

    case 'overlay':
      return (
        <div className="p-6 relative min-h-[300px]">
          {primaryImage ? (
            <img
              src={primaryImage.imageUrl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-30 rounded"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 opacity-50 rounded" />
          )}
          <div className="relative z-10">{content}</div>
        </div>
      );

    case 'full-width':
    default:
      return (
        <div className="p-6">
          {content}
          {/* Show any images below content */}
          {images.map((img) => (
            <div key={img.id} className="mt-4">
              <ImageBlock
                block={img}
                sectionId={sectionId}
                onEdit={onImageEdit}
              />
            </div>
          ))}
          {/* Show any visual blocks below content */}
          {visualBlocks.map((block) => (
            <div key={block.id} className="mt-4">
              <VisualBlockComponent
                block={block}
                sectionId={sectionId}
                onEdit={onImageEdit}
              />
            </div>
          ))}
        </div>
      );
  }
}
